/**
 * @file ユーザーアナリティクス(UA)機能全体で使用される定数、型定義、ラベル、オプションなどを定義します。
 * これには、グループ化の基準、日付オプション、フィルターの種類、デバイスカテゴリ、OSカテゴリ、ブラウザカテゴリ、
 * デフォルトチャネルグループ、フィルターの状態管理に関連する型、事前定義されたソースとメディアム、
 * セッションパフォーマンス詳細のソート順フィールド、およびグループ化フィールドなどが含まれます。
 * 国際化対応のために LinguiJS の `msg` マクロが使用されています。
 */
import { msg } from "@lingui/core/macro";
import { graphql } from "gql.tada";

// #region OrderDirection
/** ソート方向を表す型。 */
const OrderDirections = ["ASC", "DESC"] as const;
export type OrderDirection = (typeof OrderDirections)[number];
// #endregion

const CampaignLabel = msg`Campaign`;
const TermLabel = msg`Term`;
const ContentLabel = msg`Content`;

/** セッションパフォーマンスのグループ化に使用する基準を表す型。GraphQL スキーマから生成されます。 */
export type SessionPerformanceGroupBy = ReturnType<
  typeof graphql.scalar<"SessionPerformanceGroupBy">
>;
/** 各セッションパフォーマンスグループ化基準に対応する表示ラベル（国際化対応）。 */
export const SessionPerformanceGroupByLabels: Record<
  SessionPerformanceGroupBy,
  ReturnType<typeof msg>
> = {
  FIRST_PAGE_LOCATION_URL_GROUP: msg`Landing page URL group`,
  SECOND_PAGE_LOCATION_URL_GROUP: msg`Second page URL group`,
  FIRST_PAGE_TITLE: msg`Landing page title`,
  SECOND_PAGE_TITLE: msg`Second page title`,
  SOURCE_MEDIUM: msg`Source / medium`,
  CAMPAIGN: CampaignLabel,
  DEFAULT_CHANNEL_GROUP: msg`Default channel group`,
  CUSTOM_CHANNEL_GROUP: msg`Custom channel group`,
  EXP_VARIANT_STRING: msg`A/B testing variant`,
};
/** UAチャートで使用可能なグループ化基準のリスト（DEFAULT_CHANNEL_GROUPを除外）。 */
export const UaChartGroupBys = [
  "FIRST_PAGE_LOCATION_URL_GROUP",
  "SECOND_PAGE_LOCATION_URL_GROUP",
  "FIRST_PAGE_TITLE",
  "SECOND_PAGE_TITLE",
  "SOURCE_MEDIUM",
  "CAMPAIGN",
  "DEFAULT_CHANNEL_GROUP",
  "CUSTOM_CHANNEL_GROUP",
  "EXP_VARIANT_STRING",
] as const satisfies readonly SessionPerformanceGroupBy[];

/** UAチャートのグループ化基準を選択するためのオプションリスト。 */
export const UaChartGroupByOptions = UaChartGroupBys.map((groupBy) => ({
  value: groupBy,
  label: SessionPerformanceGroupByLabels[groupBy],
}));

// #region UaDateOptionSingle
/** UA機能で使用する単一の日付範囲オプションのリスト。 */
export const UaDateOptionSingles = [
  "latest",
  "7d",
  "14d",
  "28d",
  "3m",
  "custom",
  "saved",
] as const;
/** UA機能で使用する単一の日付範囲オプションを表す型。 */
export type UaDateOptionSingle = (typeof UaDateOptionSingles)[number];
/** 指定された値が UaDateOptionSingle 型であるかどうかを判定する型ガード関数。 */
export function isUaDateOptionSingle(
  value: unknown,
): value is UaDateOptionSingle {
  return UaDateOptionSingles.includes(value as UaDateOptionSingle);
}
/** 各単一日付範囲オプションに対応する表示ラベル（国際化対応）。 */
export const UaDateOptionSingleLabels: Record<
  UaDateOptionSingle,
  ReturnType<typeof msg>
> = {
  latest: msg`Latest date`,
  "7d": msg`Past 7 days`,
  "14d": msg`Past 14 days`,
  "28d": msg`Past 28 days`,
  "3m": msg`Past 90 days`,
  custom: msg`Custom`,
  saved: msg`Saved date`,
} as const;
/** 単一日付範囲オプションを選択するためのオプションリスト。 */
export const UaDateOptionSingleOptions = UaDateOptionSingles.map((option) => ({
  value: option,
  label: UaDateOptionSingleLabels[option],
}));
// #endregion

// #region UaDateOptionComparison
/** UA機能で使用する比較日付範囲オプションのリスト。 */
export const UaDateOptionComparisons = [
  "comp_7d",
  "comp_14d",
  "comp_28d",
  "comp_3m",
  "comp_this_month",
  "comp_last_month",
  "comp_custom",
] as const;
/** UA機能で使用する比較日付範囲オプションを表す型。 */
export type UaDateOptionComparison = (typeof UaDateOptionComparisons)[number];
/** 指定された値が UaDateOptionComparison 型であるかどうかを判定する型ガード関数。 */
export function isUaDateOptionComparison(
  value: unknown,
): value is UaDateOptionComparison {
  return UaDateOptionComparisons.includes(value as UaDateOptionComparison);
}
/** 各比較日付範囲オプションに対応する表示ラベル（国際化対応）。 */
const UaDateOptionComparisonLabels: Record<
  UaDateOptionComparison,
  ReturnType<typeof msg>
> = {
  comp_7d: msg`Past 7 days vs previous 7 days`,
  comp_14d: msg`Past 14 days vs previous 14 days`,
  comp_28d: msg`Past 28 days vs previous 28 days`,
  comp_3m: msg`Past 90 days vs previous 90 days`,
  comp_this_month: msg`Current month vs same month last year`,
  comp_last_month: msg`Previous month vs same month last year`,
  comp_custom: msg`Custom Comparison`,
} as const;

/** 比較日付範囲オプションを選択するためのオプションリスト。 */
export const UaDateOptionComparisonOptions = UaDateOptionComparisons.map(
  (option) => ({
    value: option,
    label: UaDateOptionComparisonLabels[option],
  }),
);
// #endregion

// #region DateOption
/** UA機能で使用する日付範囲オプション（単一または比較）を表す型。 */
export type UaDateOption = UaDateOptionSingle | UaDateOptionComparison;
/** 指定された値が UaDateOption 型であるかどうかを判定する型ガード関数。 */
function _isUaDateOption(value: unknown): value is UaDateOption {
  return isUaDateOptionSingle(value) || isUaDateOptionComparison(value);
}
// #endregion

// #region UserAnalyticsDataDateType
/** ユーザーアナリティクスデータの取得期間を指定する型。GraphQL スキーマから生成されます。 */
export type UserAnalyticsDataDateType = ReturnType<
  typeof graphql.scalar<"UserAnalyticsDataDateType">
>;
/** 各データ取得期間に対応する表示ラベル（国際化対応）。 */
export const UserAnalyticsDataDateTypeLabels: Record<
  UserAnalyticsDataDateType,
  ReturnType<typeof msg>
> = {
  LATEST: msg`Latest`,
  DAYS_7: msg`Past 7 days`,
  DAYS_14: msg`Past 14 days`,
  DAYS_28: msg`Past 28 days`,
  DAYS_90: msg`Past 90 days`,
  DAYS_180: msg`Past 180 days`,
  DAYS_365: msg`Past 365 days`,
  DAYS_480: msg`Past 480 days`,
  MONTHS_3: msg`Past 90 days`,
  MONTHS_6: msg`Past 180 days`,
  MONTHS_12: msg`Past 365 days`,
  MONTHS_16: msg`Past 480 days`,
  CUSTOM: msg`Custom`,
};
// #endregion

// #region UserAnalyticsFilter
/** ユーザーアナリティクスのフィルター条件の比較演算子を表す型。GraphQL スキーマから生成されます。 */
export type UserAnalyticsFilter = ReturnType<
  typeof graphql.scalar<"UserAnalyticsFilter">
>;
/** ユーザーアナリティクスのフィルター条件として使用可能な比較演算子のリスト。 */
const UserAnalyticsFilterValues = [
  "EQUALS",
  "CONTAINS",
  "NOT_CONTAINS",
  "STARTS_WITH",
  "ENDS_WITH",
  "REGEX",
  "NOT_REGEX",
] as const;
/** 各フィルター比較演算子に対応する表示ラベル（国際化対応）。 */
export const UserAnalyticsFilterLabels: Record<
  UserAnalyticsFilter,
  ReturnType<typeof msg>
> = {
  EQUALS: msg`Equals`,
  CONTAINS: msg`Contains`,
  NOT_CONTAINS: msg`Not contains`,
  STARTS_WITH: msg`Starts with`,
  ENDS_WITH: msg`Ends with`,
  REGEX: msg`Regex`,
  NOT_REGEX: msg`Not regex`,
} as const;
/** フィルターの比較演算子を選択するためのUIオプションリスト。 */
export const UserAnalyticsFilterOptions = UserAnalyticsFilterValues.map(
  (value) => ({ value, label: UserAnalyticsFilterLabels[value] }),
);
/** 指定された値が UserAnalyticsFilter 型であるかどうかを判定する型ガード関数。 */
export function isUserAnalyticsFilter(
  value: unknown,
): value is UserAnalyticsFilter {
  return UserAnalyticsFilterValues.includes(value as UserAnalyticsFilter);
}
// #endregion

// #region OSCategory
/** OSのカテゴリを表す型。GraphQL スキーマから生成されます。 */
export type OSCategory = ReturnType<typeof graphql.scalar<"OSCategory">>;
/** OSのカテゴリとして指定可能な値のリスト。 */
export const OSCategoryValues = [
  "ANDROID",
  "IOS",
  "WINDOWS",
  "MACOS",
  "LINUX",
  "CHROME_OS",
  "OTHER",
] as const;
/** 各OSカテゴリに対応する表示ラベル（国際化対応）。 */
export const OSCategoryLabels: Record<OSCategory, ReturnType<typeof msg>> = {
  ANDROID: msg`Android`,
  IOS: msg`iOS`,
  WINDOWS: msg`Windows`,
  MACOS: msg`Macintosh`,
  LINUX: msg`Linux`,
  CHROME_OS: msg`Chrome OS`,
  OTHER: msg`Other`,
} as const;
/** OSカテゴリを選択するためのUIオプションリスト。 */
export const OSCategoryOptions = OSCategoryValues.map((value) => ({
  value,
  label: OSCategoryLabels[value],
}));
/** 指定された値が OSCategory 型であるかどうかを判定する型ガード関数。 */
export function isOSCategory(value: unknown): value is OSCategory {
  return OSCategoryValues.includes(value as OSCategory);
}
/** APIレスポンスの表示文字列からOSCategoryへの変換マッピング */
export const OSDisplayToEnum: Record<string, OSCategory> = {
  Android: "ANDROID",
  iOS: "IOS",
  Windows: "WINDOWS",
  Macintosh: "MACOS",
  Linux: "LINUX",
  "Chrome OS": "CHROME_OS",
} as const;
// #endregion

// #region DeviceCategory
/** デバイスのカテゴリを表す型。GraphQL スキーマから生成されます。 */
export type DeviceCategory = ReturnType<
  typeof graphql.scalar<"DeviceCategory">
>;
/** デバイスのカテゴリとして指定可能な値のリスト。 */
const DeviceCategoryValues = [
  "MOBILE",
  "DESKTOP",
  "TABLET",
  "SMART_TV",
  "OTHER",
] as const;
/** 各デバイスカテゴリに対応する表示ラベル（国際化対応）。 */
export const DeviceCategoryLabels: Record<
  DeviceCategory,
  ReturnType<typeof msg>
> = {
  MOBILE: msg`Mobile`,
  DESKTOP: msg`Desktop`,
  TABLET: msg`Tablet`,
  SMART_TV: msg`Smart TV`,
  OTHER: msg`Other`,
} as const;
/** デバイスカテゴリを選択するためのUIオプションリスト。 */
export const DeviceCategoryOptions = DeviceCategoryValues.map((value) => ({
  value,
  label: DeviceCategoryLabels[value],
}));
/** 指定された値が DeviceCategory 型であるかどうかを判定する型ガード関数。 */
export function isDeviceCategory(value: unknown): value is DeviceCategory {
  return DeviceCategoryValues.includes(value as DeviceCategory);
}
/** APIレスポンスの表示文字列からDeviceCategoryへの変換マッピング */
export const DeviceDisplayToEnum: Record<string, DeviceCategory> = {
  mobile: "MOBILE",
  desktop: "DESKTOP",
  tablet: "TABLET",
  "smart tv": "SMART_TV",
} as const;
// #endregion

// #region BrowserCategory
/** ブラウザのカテゴリを表す型。GraphQL スキーマから生成されます。 */
export type BrowserCategory = ReturnType<
  typeof graphql.scalar<"BrowserCategory">
>;
/** ブラウザのカテゴリとして指定可能な値のリスト。 */
const BrowserCategoryValues = [
  "CHROME",
  "FIREFOX",
  "SAFARI",
  "EDGE",
  "SAFARI_IN_APP",
  "ANDROID_WEBVIEW",
  "OPERA",
  "OTHER",
] as const;
/** 各ブラウザカテゴリに対応する表示ラベル（国際化対応）。 */
export const BrowserCategoryLabels: Record<
  BrowserCategory,
  ReturnType<typeof msg>
> = {
  CHROME: msg`Chrome`,
  FIREFOX: msg`Firefox`,
  SAFARI: msg`Safari`,
  EDGE: msg`Edge`,
  SAFARI_IN_APP: msg`Safari In-App`,
  ANDROID_WEBVIEW: msg`Android WebView`,
  OPERA: msg`Opera`,
  OTHER: msg`Other`,
} as const;
/** ブラウザカテゴリを選択するためのUIオプションリスト。 */
export const BrowserCategoryOptions = BrowserCategoryValues.map((value) => ({
  value,
  label: BrowserCategoryLabels[value],
}));
/** 指定された値が BrowserCategory 型であるかどうかを判定する型ガード関数。 */
export function isBrowserCategory(value: unknown): value is BrowserCategory {
  return BrowserCategoryValues.includes(value as BrowserCategory);
}
/** APIレスポンスの表示文字列からBrowserCategoryへの変換マッピング */
export const BrowserDisplayToEnum: Record<string, BrowserCategory> = {
  Chrome: "CHROME",
  Firefox: "FIREFOX",
  Safari: "SAFARI",
  Edge: "EDGE",
  "Safari (in-app)": "SAFARI_IN_APP",
  "Android WebView": "ANDROID_WEBVIEW",
  Opera: "OPERA",
} as const;
// #endregion

// #region DefaultChannelGroup
/** デフォルトチャネルグループを表す型。GraphQL スキーマから生成されます。 */
export type DefaultChannelGroup = ReturnType<
  typeof graphql.scalar<"DefaultChannelGroup">
>;
/** デフォルトチャネルグループとして指定可能な値のリスト。 */
export const DefaultChannelGroupValues = [
  "AFFILIATES",
  "AI_ASSISTANT",
  "AUDIO",
  "CROSS_NETWORK",
  "DIRECT",
  "DISPLAY",
  "EMAIL",
  "MOBILE_PUSH_NOTIFICATIONS",
  "ORGANIC_SEARCH",
  "ORGANIC_SHOPPING",
  "ORGANIC_SOCIAL",
  "ORGANIC_VIDEO",
  "PAID_OTHER",
  "PAID_SEARCH",
  "PAID_SHOPPING",
  "PAID_SOCIAL",
  "PAID_VIDEO",
  "REFERRAL",
  "SMS",
] as const;
/** 各デフォルトチャネルグループに対応する表示ラベル（国際化対応）。 */
export const DefaultChannelGroupLabels: Record<
  DefaultChannelGroup,
  ReturnType<typeof msg>
> = {
  AFFILIATES: msg`Affiliates`,
  AI_ASSISTANT: msg`AI Assistant`,
  AUDIO: msg`Audio`,
  CROSS_NETWORK: msg`Cross Network`,
  DIRECT: msg`Direct`,
  DISPLAY: msg`Display`,
  EMAIL: msg`Email`,
  MOBILE_PUSH_NOTIFICATIONS: msg`Mobile Push Notifications`,
  ORGANIC_SEARCH: msg`Organic Search`,
  ORGANIC_SHOPPING: msg`Organic Shopping`,
  ORGANIC_SOCIAL: msg`Organic Social`,
  ORGANIC_VIDEO: msg`Organic Video`,
  PAID_OTHER: msg`Paid Other`,
  PAID_SEARCH: msg`Paid Search`,
  PAID_SHOPPING: msg`Paid Shopping`,
  PAID_SOCIAL: msg`Paid Social`,
  PAID_VIDEO: msg`Paid Video`,
  REFERRAL: msg`Referral`,
  SMS: msg`SMS`,
} as const;
/** BigQuery の `default_channel_group` 列に保存される値。 */
export const DefaultChannelGroupBigQueryValues: Record<
  DefaultChannelGroup,
  string
> = {
  AFFILIATES: "Affiliates",
  AI_ASSISTANT: "AI Assistant",
  AUDIO: "Audio",
  CROSS_NETWORK: "Cross-network",
  DIRECT: "Direct",
  DISPLAY: "Display",
  EMAIL: "Email",
  MOBILE_PUSH_NOTIFICATIONS: "Mobile Push Notifications",
  ORGANIC_SEARCH: "Organic Search",
  ORGANIC_SHOPPING: "Organic Shopping",
  ORGANIC_SOCIAL: "Organic Social",
  ORGANIC_VIDEO: "Organic Video",
  PAID_OTHER: "Paid Other",
  PAID_SEARCH: "Paid Search",
  PAID_SHOPPING: "Paid Shopping",
  PAID_SOCIAL: "Paid Social",
  PAID_VIDEO: "Paid Video",
  REFERRAL: "Referral",
  SMS: "SMS",
};
/** デフォルトチャネルグループを選択するためのUIオプションリスト。 */
export const DefaultChannelGroupOptions = DefaultChannelGroupValues.map(
  (value) => ({
    value,
    label: DefaultChannelGroupLabels[value],
  }),
);
/** BigQuery の値で比較する条件向けの選択肢。 */
export const DefaultChannelGroupBigQueryOptions = DefaultChannelGroupValues.map(
  (enumValue) => ({
    enumValue,
    label: DefaultChannelGroupLabels[enumValue],
    value: DefaultChannelGroupBigQueryValues[enumValue],
  }),
);

export function findDefaultChannelGroupBigQueryOption(value: string) {
  return DefaultChannelGroupBigQueryOptions.find(
    (option) => option.value === value || option.enumValue === value,
  );
}
/** 指定された値が DefaultChannelGroup 型であるかどうかを判定する型ガード関数。 */
export function isDefaultChannelGroup(
  value: unknown,
): value is DefaultChannelGroup {
  return DefaultChannelGroupValues.includes(value as DefaultChannelGroup);
}
// #endregion

// #region SessionWithNavigation
/** 回遊フィルターの選択肢を表す型。 */
const SessionWithNavigationOptions = ["none", "true", "false"] as const;
/** 回遊フィルターの選択肢の型定義。 */
export type SessionWithNavigationOption =
  (typeof SessionWithNavigationOptions)[number];
export function isSessionWithNavigationOption(
  value: unknown,
): value is SessionWithNavigationOption {
  return SessionWithNavigationOptions.includes(
    value as SessionWithNavigationOption,
  );
}
/** 各回遊フィルター選択肢に対応する表示ラベル（国際化対応）。 */
export const SessionWithNavigationOptionLabels: Record<
  SessionWithNavigationOption,
  ReturnType<typeof msg>
> = {
  none: msg`Not specified`,
  true: msg`Sessions with surf activity`,
  false: msg`Sessions without surf activity`,
};
// #endregion

// #region PageLocationCount
/** 閲覧ページ数フィルターの値を表す型。 */
export type PageLocationCountValue = {
  min?: string;
  max?: string;
};
// #endregion

// #region GaSessionNumber
/** GAセッション番号フィルターの値を表す型。 */
export type GaSessionNumberValue = {
  min?: string;
  max?: string;
};
// #endregion

// #region RevenueRange
/** 売上フィルターの値を表す型。 */
export type RevenueRangeValue = {
  min?: string;
  max?: string;
};
// #endregion

// #region UaFilterState
/** UA機能のフィルター状態を表す型。複数のフィルター条件を配列で保持します。 */
export type UaFilterState = (
  | {
      field: "firstPageLocation";
      value: { type: UserAnalyticsFilter; pattern: string };
    }
  | { field: "firstPageLocationUrlGroupIds"; value: string[] }
  | {
      field: "firstPageTitle";
      value: { type: UserAnalyticsFilter; pattern: string };
    }
  | {
      field: "secondPageLocation";
      value: { type: UserAnalyticsFilter; pattern: string };
    }
  | { field: "secondPageLocationUrlGroupIds"; value: string[] }
  | {
      field: "secondPageTitle";
      value: { type: UserAnalyticsFilter; pattern: string };
    }
  | { field: "devices"; value: DeviceCategory[] }
  | { field: "os"; value: OSCategory[] }
  | { field: "browsers"; value: BrowserCategory[] }
  | { field: "sources"; value: string[] }
  | { field: "mediums"; value: string[] }
  | { field: "countries"; value: string[] }
  | { field: "regions"; value: string[] }
  | { field: "cities"; value: string[] }
  | {
      field: "campaigns";
      value: { type: UserAnalyticsFilter; pattern: string }[];
    }
  | { field: "events"; value: string[] }
  | { field: "eventsNotIn"; value: string[] }
  | {
      field: "userPseudoId";
      value: { type: UserAnalyticsFilter; pattern: string };
    }
  | {
      field: "sessionId";
      value: { type: UserAnalyticsFilter; pattern: string };
    }
  | { field: "defaultChannelGroup"; value: DefaultChannelGroup[] }
  | {
      field: "customChannelGroup";
      value: { type: UserAnalyticsFilter; pattern: string }[];
    }
  | { field: "gaSessionNumber"; value: GaSessionNumberValue }
  | { field: "revenueRange"; value: RevenueRangeValue }
  | { field: "isSessionWithNavigation"; value: SessionWithNavigationOption }
  | { field: "pageLocationCount"; value: PageLocationCountValue }
  | {
      field: "visitedPage";
      value: { type: UserAnalyticsFilter; pattern: string };
    }
  | { field: "expVariantString"; value: string[] }
)[];
/** 配列形式の値を持つUAフィルターフィールドのリスト。 */
export const UaArrayFilterFields = [
  "firstPageLocationUrlGroupIds",
  "secondPageLocationUrlGroupIds",
  "devices",
  "os",
  "browsers",
  "sources",
  "mediums",
  "countries",
  "regions",
  "cities",
  "events",
  "eventsNotIn",
  "defaultChannelGroup",
  "expVariantString",
] as const;
/** 配列形式の値を持つUAフィルターフィールドを表す型。 */
export type UaArrayFilterField = (typeof UaArrayFilterFields)[number];
/** 指定された値が UaArrayFilterField 型であるかどうかを判定する型ガード関数。 */
export function isUaArrayFilterField(
  value: unknown,
): value is UaArrayFilterField {
  return UaArrayFilterFields.includes(value as UaArrayFilterField);
}
/** オブジェクト形式の値（type と pattern を持つ）を持つUAフィルターフィールドのリスト。 */
export const UaObjectFilterFields = [
  "firstPageLocation",
  "secondPageLocation",
  "firstPageTitle",
  "secondPageTitle",
  "visitedPage",
  "userPseudoId",
  "sessionId",
] as const;
/** オブジェクト形式の値を持つUAフィルターフィールドを表す型。 */
export type UaObjectFilterField = (typeof UaObjectFilterFields)[number];
/** 指定された値が UaObjectFilterField 型であるかどうかを判定する型ガード関数。 */
export function isUaObjectFilterField(
  value: unknown,
): value is UaObjectFilterField {
  return UaObjectFilterFields.includes(value as UaObjectFilterField);
}
/** オブジェクトの配列形式の値を持つUAフィルターフィールドのリスト。 */
export const UaObjectArrayFilterFields = [
  "campaigns",
  "customChannelGroup",
] as const;
/** オブジェクトの配列形式の値を持つUAフィルターフィールドを表す型。 */
export type UaObjectArrayFilterField =
  (typeof UaObjectArrayFilterFields)[number];
/** 指定された値が UaObjectArrayFilterField 型であるかどうかを判定する型ガード関数。 */
export function isUaObjectArrayFilterField(
  value: unknown,
): value is UaObjectArrayFilterField {
  return UaObjectArrayFilterFields.includes(value as UaObjectArrayFilterField);
}
/** boolean形式の値を持つUAフィルターフィールドのリスト。 */
export const UaBooleanFilterFields = ["isSessionWithNavigation"] as const;
/** boolean形式の値を持つUAフィルターフィールドを表す型。 */
export type UaBooleanFilterField = (typeof UaBooleanFilterFields)[number];
/** 指定された値が UaBooleanFilterField 型であるかどうかを判定する型ガード関数。 */
export function isUaBooleanFilterField(
  value: unknown,
): value is UaBooleanFilterField {
  return UaBooleanFilterFields.includes(value as UaBooleanFilterField);
}
/** 範囲オブジェクト形式の値を持つUAフィルターフィールドのリスト。 */
export const UaRangeFilterFields = [
  "gaSessionNumber",
  "pageLocationCount",
  "revenueRange",
] as const;
/** 範囲オブジェクト形式の値を持つUAフィルターフィールドを表す型。 */
export type UaRangeFilterField = (typeof UaRangeFilterFields)[number];
/** 指定された値が UaRangeFilterField 型であるかどうかを判定する型ガード関数。 */
export function isUaRangeFilterField(
  value: unknown,
): value is UaRangeFilterField {
  return UaRangeFilterFields.includes(value as UaRangeFilterField);
}
/** UAフィルターフィールドのすべての種類を網羅する型。 */
export type UaFilterField =
  | UaArrayFilterField
  | UaObjectFilterField
  | UaObjectArrayFilterField
  | UaBooleanFilterField
  | UaRangeFilterField;
/** 各UAフィルターフィールドに対応する表示ラベル（国際化対応）。 */
export const UaFilterFieldLabels: Record<
  UaFilterField,
  ReturnType<typeof msg>
> = {
  firstPageLocation: msg`Landing page`,
  secondPageLocation: msg`Second page`,
  firstPageTitle: msg`Landing page title`,
  secondPageTitle: msg`Second page title`,
  firstPageLocationUrlGroupIds: msg`Landing page URL group`,
  secondPageLocationUrlGroupIds: msg`Second page URL group`,
  devices: msg`Devices`,
  os: msg`OS`,
  browsers: msg`Browsers`,
  sources: msg`Sources`,
  mediums: msg`Mediums`,
  countries: msg`Country`,
  regions: msg`Region`,
  cities: msg`City`,
  campaigns: msg`Campaigns`,
  events: msg`Events`,
  eventsNotIn: msg`Events to Exclude`,
  userPseudoId: msg`User ID`,
  sessionId: msg`Session ID`,
  defaultChannelGroup: msg`Default channel group`,
  customChannelGroup: msg`Custom channel group`,
  gaSessionNumber: msg`Session Number`,
  revenueRange: msg`Revenue`,
  isSessionWithNavigation: msg`Surf`,
  pageLocationCount: msg`Page Location Count`,
  visitedPage: msg`Visited Page`,
  expVariantString: msg`AB Testing`,
} as const;
// #endregion

// #region Source
/** 事前定義されたソース（参照元）のリスト。フィルターのサジェストなどに使用されます。 */
export const PredefinedSources = [
  "google",
  "yahoo",
  "bing",
  "newsletter",
  "facebook",
  "m.facebook.com",
  "meta",
  "instagram",
  "l.instagram.com",
  "twitter",
  "t.co",
  "line",
  "baidu",
  "linkedin",
  "email",
  "banner",
  "affiliate",
  "(direct)",
];
// #endregion

export const PredefinedAiSources = [
  "chatgpt.com",
  "perplexity.ai",
  "gemini.google.com",
  "copilot.microsoft.com",
  "openai.com",
  "claude.ai",
  "writesonic.com",
  "copy.ai",
  "deepseek.com",
  "bard.google.com",
  "jasper.ai",
  "khanmigo.ai",
  "pi.ai",
  "huggingface.co",
  "character.ai",
  "poe.com",
  "llama.com",
  "textcortex.com",
  "botsonic.com",
  "midjourney.com",
  "stability.ai",
  "replit.com",
  "cohere.com",
  "anthropic.com",
  "phind.com",
];

// #region Medium
/** 事前定義されたメディアムのリスト。フィルターのサジェストなどに使用されます。 */
export const PredefinedMediums = [
  "cpc",
  "email",
  "social",
  "organic",
  "referral",
  "display",
  "affiliate",
  "(none)",
  "(not set)",
];
// #endregion

// #region SessionPerformanceDetailOrderBy
/** セッションパフォーマンス詳細データのソート順を指定するフィールドを表す型。GraphQL スキーマから生成されます。 */
export type SessionPerformanceDetailOrderByField = ReturnType<
  typeof graphql.scalar<"SessionPerformanceDetailOrderByField">
>;
/** 各ソート順フィールドに対応する表示ラベル（国際化対応）。 */
export const SessionPerformanceDetailOrderByFieldLabels: Record<
  SessionPerformanceDetailOrderByField,
  ReturnType<typeof msg>
> = {
  DATE: msg`Date`,
  WEEK: msg`Week`,
  MONTH: msg`Month`,
  FIRST_PAGE_LOCATION: msg`Landing page`,
  FIRST_PAGE_LOCATION_URL_GROUP: msg`Landing page URL group`,
  FIRST_PAGE_TITLE: msg`Landing page title`,
  SECOND_PAGE_LOCATION: msg`Second page`,
  SECOND_PAGE_LOCATION_URL_GROUP: msg`Second page URL group`,
  SECOND_PAGE_TITLE: msg`Second page title`,
  DEVICE: msg`Device`,
  OS: msg`OS`,
  BROWSER: msg`Browser`,
  SOURCE_MEDIUM: msg`Source / medium`,
  CAMPAIGN: CampaignLabel,
  TERM: TermLabel,
  CONTENT: ContentLabel,
  DEFAULT_CHANNEL_GROUP: msg`Default channel group`,
  CUSTOM_CHANNEL_GROUP: msg`Custom channel group`,
  EXP_VARIANT_STRING: msg`A/B testing variant`,
  COUNTRY: msg`Country`,
  REGION: msg`Region`,
  CITY: msg`City`,
  SESSION_COUNT: msg`Sessions`,
  UNIQUE_USER_COUNT: msg`Unique users`,
  FIRST_SESSION_RATE: msg`First session rate`,
  CONVERSION_RATE: msg`Conversion rate`,
  CONVERSION_COUNT: msg`Sessions with conversions`,
  SURF_RATE: msg`Surf rate`,
  SURF_COUNT: msg`Surf count`,
  AVG_SESSION_DURATION: msg`Avg. session duration`,
  ENGAGEMENT_RATE: msg`Engagement rate`,
  ENGAGEMENT_COUNT: msg`Engagements`,
  AVG_PAGE_LOCATION_COUNT: msg`Avg. page locations`,
  PAGE_VIEW_EVENT_COUNT: msg`Page views`,
  REVENUE: msg`Revenue`,
  TRANSACTION_COUNT: msg`Transactions`,
  PURCHASE_RATE: msg`Purchase rate`,
  AVERAGE_ORDER_VALUE: msg`Average order value`,
  REFUND_VALUE: msg`Refund value`,
  ITEMS_PURCHASED: msg`Items purchased`,
} as const;

const HiddenSessionPerformanceDetailOrderByFields: ReadonlySet<SessionPerformanceDetailOrderByField> =
  new Set(["TERM", "CONTENT"]);

/** セッションパフォーマンス詳細データのソート順として指定可能なフィールドのリスト。 */
export const SessionPerformanceDetailOrderByFieldValues = (
  Object.keys(
    SessionPerformanceDetailOrderByFieldLabels,
  ) as SessionPerformanceDetailOrderByField[]
).filter((field) => !HiddenSessionPerformanceDetailOrderByFields.has(field));
/** セッションパフォーマンス詳細データのソート順フィールドを選択するためのオプションリスト。 */
export const SessionPerformanceDetailOrderByFieldOptions =
  SessionPerformanceDetailOrderByFieldValues.map((field) => ({
    value: field,
    label: SessionPerformanceDetailOrderByFieldLabels[field],
  }));
/** 期間比較用のソート順フィールドを選択するためのオプションリスト（DATE、WEEK、MONTHを除外）。 */
export const SessionPerformanceDetailComparingOrderByFieldOptions =
  SessionPerformanceDetailOrderByFieldValues.filter(
    (field) => field !== "DATE" && field !== "WEEK" && field !== "MONTH",
  ).map((field) => ({
    value: field,
    label: SessionPerformanceDetailOrderByFieldLabels[field],
  }));
const EcommerceSessionPerformanceDetailOrderByFields: ReadonlySet<SessionPerformanceDetailOrderByField> =
  new Set<SessionPerformanceDetailOrderByField>([
    "REVENUE",
    "TRANSACTION_COUNT",
    "PURCHASE_RATE",
    "AVERAGE_ORDER_VALUE",
    "REFUND_VALUE",
    "ITEMS_PURCHASED",
  ]);

export function isEcommerceSessionPerformanceDetailOrderByField(
  field: SessionPerformanceDetailOrderByField,
): boolean {
  return EcommerceSessionPerformanceDetailOrderByFields.has(field);
}
/** 指定された値が SessionPerformanceDetailOrderByField 型であるかどうかを判定する型ガード関数。 */
export function isSessionPerformanceDetailOrderByField(
  value: unknown,
): value is SessionPerformanceDetailOrderByField {
  return SessionPerformanceDetailOrderByFieldValues.includes(
    value as SessionPerformanceDetailOrderByField,
  );
}

/** セッションパフォーマンス詳細データのソート順を指定するオブジェクトの型。 */
export type SessionPerformanceDetailOrderByInput = {
  field: SessionPerformanceDetailOrderByField;
  direction: OrderDirection;
};
// #endregion

// #region SessionPerformanceDetailGroupBy
/** セッションパフォーマンス詳細データのグループ化に使用する基準を表す型。GraphQL スキーマから生成されます。 */
export type SessionPerformanceDetailGroupBy = ReturnType<
  typeof graphql.scalar<"SessionPerformanceDetailGroupBy">
>;
/** 各セッションパフォーマンス詳細データグループ化基準に対応する表示ラベル（国際化対応）。 */
export const SessionPerformanceDetailGroupByLabels: Record<
  SessionPerformanceDetailGroupBy,
  ReturnType<typeof msg>
> = {
  DATE: msg`Date`,
  WEEK: msg`Week`,
  MONTH: msg`Month`,
  DEFAULT_CHANNEL_GROUP: msg`Default channel group`,
  CUSTOM_CHANNEL_GROUP: msg`Custom channel group`,
  SOURCE_MEDIUM: msg`Source / medium`,
  CAMPAIGN: CampaignLabel,
  TERM: TermLabel,
  CONTENT: ContentLabel,
  FIRST_PAGE_LOCATION: msg`Landing page`,
  FIRST_PAGE_LOCATION_URL_GROUP: msg`Landing page URL group`,
  FIRST_PAGE_TITLE: msg`Landing page title`,
  SECOND_PAGE_LOCATION: msg`Second page`,
  SECOND_PAGE_LOCATION_URL_GROUP: msg`Second page URL group`,
  SECOND_PAGE_TITLE: msg`Second page title`,
  DEVICE: msg`Device`,
  OS: msg`OS`,
  BROWSER: msg`Browser`,
  EXP_VARIANT_STRING: msg`A/B testing variant`,
  COUNTRY: msg`Country`,
  REGION: msg`Region`,
  CITY: msg`City`,
} as const;
/** セッションパフォーマンス詳細データのグループ化基準として指定可能な値のリスト。 */
export const SessionPerformanceDetailGroupByValues = Object.keys(
  SessionPerformanceDetailGroupByLabels,
) as SessionPerformanceDetailGroupBy[];
/** セッションパフォーマンス詳細データのグループ化基準を選択するためのUIオプションリスト。 */
export const SessionPerformanceDetailGroupByOptions =
  SessionPerformanceDetailGroupByValues.map((value) => ({
    value,
    label: SessionPerformanceDetailGroupByLabels[value],
  }));
// #endregion
